(() => {
  const wired = new WeakSet();
  const init = (root = document) => {
    root.querySelectorAll('.wl-form').forEach((form) => {
      if (wired.has(form)) return;
      wired.add(form);
      form.addEventListener('submit', (event) => {
        const button = form.querySelector('[data-wl-submit]');
        if (!button) return;
        if (!form.checkValidity()) return;
        queueMicrotask(() => {
          if (event.defaultPrevented) return;
          button.dataset.originalLabel = button.firstElementChild.textContent;
          button.setAttribute('aria-busy', 'true');
          button.firstElementChild.textContent = button.dataset.loadingLabel;
        });
      });
    });
    root.querySelectorAll('[data-wl-preview]').forEach((link) => {
      if (wired.has(link)) return;
      wired.add(link);
      link.addEventListener('click', () => {
        const panel = document.getElementById('PrivatePreview');
        if (!panel) return;
        panel.open = true;
        panel.querySelector('input')?.focus();
      });
    });
    root.querySelectorAll('[data-wl-result]').forEach((result) => result.focus());
    if (location.hash === '#PrivatePreview') {
      const panel = document.getElementById('PrivatePreview');
      if (panel) { panel.open = true; panel.querySelector('input')?.focus(); }
    }
  };
  init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  window.addEventListener('pageshow', () => {
    document.querySelectorAll('.wl-form').forEach((form) => {
      delete form.dataset.submitting;
      const button = form.querySelector('[data-wl-submit]');
      if (button) {
        button.removeAttribute('aria-busy');
        if (button.dataset.originalLabel) button.firstElementChild.textContent = button.dataset.originalLabel;
      }
    });
  });
})();

if (!customElements.get('vobara-waitlist-film')) {
  customElements.define('vobara-waitlist-film', class extends HTMLElement {
    connectedCallback() {
      this.video = this.querySelector('video');
      this.button = this.querySelector('button');
      if (!this.video || !this.button || this.controller) return;
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.userPaused = false;
      this.inView = false;
      this.failed = false;
      this.video.muted = true;
      this.video.defaultMuted = true;
      this.video.addEventListener('playing', () => {
        const reveal = () => {
          if (!this.isConnected || this.failed) return;
          this.dataset.frame = '';
          this.button.hidden = false;
          this.updateButton();
        };
        if (this.video.requestVideoFrameCallback) this.video.requestVideoFrameCallback(reveal);
        else reveal();
      }, options);
      this.video.addEventListener('pause', () => this.updateButton(), options);
      this.video.addEventListener('error', () => {
        this.failed = true;
        this.dataset.failed = '';
        delete this.dataset.frame;
        this.button.hidden = true;
      }, options);
      this.button.addEventListener('click', () => {
        this.userPaused = !this.video.paused;
        if (this.userPaused) this.video.pause();
        else this.play();
      }, options);
      document.addEventListener('visibilitychange', () => this.sync(), options);
      this.motion.addEventListener('change', () => this.sync(), options);
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(([entry]) => {
          this.inView = entry.isIntersecting;
          this.sync();
        }, { threshold: 0.05 });
        this.observer.observe(this);
      } else {
        this.inView = true;
        this.sync();
      }
    }
    play() {
      if (this.failed) return;
      if (!this.video.getAttribute('src')) this.video.src = this.video.dataset.src;
      this.video.play().catch(() => this.updateButton());
    }
    sync() {
      if (this.inView && !document.hidden && !this.motion.matches && !this.userPaused) this.play();
      else this.video.pause();
    }
    updateButton() {
      const paused = this.video.paused;
      this.toggleAttribute('data-paused', paused);
      this.button.setAttribute('aria-label', paused ? this.button.dataset.resumeLabel : this.button.dataset.pauseLabel);
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.video?.pause();
      this.controller = null;
    }
  });
}

/*
 * Shopify Forms owns validation, consent and submission. This small adapter only
 * passes an email through its visible input's normal input/change events.
 * The vendor does not document a dynamic prefill API. Keep this boundary covered
 * by browser checks when updating Forms; if its email field changes, leave the
 * complete inline form available instead of hiding registration.
 */
if (!customElements.get('vobara-waitlist-profile')) {
  customElements.define('vobara-waitlist-profile', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.dialog = this.querySelector('dialog');
      this.starter = this.querySelector('[data-profile-start]');
      this.startEmail = this.starter?.querySelector('input[type="email"]');
      this.closeButton = this.querySelector('[data-profile-close]');
      if (!this.dialog || !this.starter || !this.startEmail || !this.closeButton || !this.dialog.showModal) return;
      this.acceptedEmails = new Set();
      this.installEmailCheck(this.starter, () => this.startEmail, this.startEmail);

      // Forms mounts asynchronously inside its open shadow root. A bounded wait
      // keeps the original inline experience available on slow or failed loads.
      let attempts = 0;
      const enhance = () => {
        const embed = this.querySelector('shopify-forms-embed');
        const root = embed?.shadowRoot;
        const email = root?.querySelector('input[name="email"], input#email, input[type="email"]');
        if (!email) return false;
        this.formRoot = root;
        this.emailField = email;
        this.installEmailCheck(root, () => root.querySelector('input[name="email"], input#email, input[type="email"]'), this.dialog.querySelector('.wl-profile-form'), true);
        this.dialog.dataset.enhanced = '';
        this.dialog.close();
        this.querySelector('[data-profile-header]').hidden = false;
        this.starter.hidden = false;
        return true;
      };
      if (!enhance()) {
        this.readyTimer = setInterval(() => {
          if (enhance() || ++attempts >= 100) clearInterval(this.readyTimer);
        }, 100);
      }

      const options = { signal: this.controller.signal };
      this.starter.addEventListener('submit', (event) => {
        event.preventDefault();
        this.startEmail.value = this.startEmail.value.trim();
        if (!this.starter.reportValidity()) return;
        const email = this.formRoot?.querySelector('input[name="email"], input#email, input[type="email"]');
        this.dialog.showModal();
        this.active = true;
        document.documentElement.classList.add('wl-profile-scroll-lock');
        if (email) {
          // Using the platform setter emits the same change path as typing;
          // never reach into React state or change the vendor submission code.
          const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
          setter.call(email, this.startEmail.value);
          email.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
          email.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
          this.emailField = email;
          requestAnimationFrame(() => {
            const firstName = this.formRoot.querySelector('input[name="first_name"], input#first_name');
            (firstName || email).focus({ preventScroll: true });
          });
        }
      }, options);
      this.closeButton.addEventListener('click', () => this.dialog.close(), options);
      this.dialog.addEventListener('close', () => {
        if (!this.active) return;
        this.active = false;
        document.documentElement.classList.remove('wl-profile-scroll-lock');
        if (this.emailField?.isConnected) this.startEmail.value = this.emailField.value;
        this.startEmail.focus({ preventScroll: true });
      }, options);
    }
    installEmailCheck(root, getEmail, anchor, before = false) {
      // Suggest only known provider misspellings. Never rewrite an address or
      // guess a person's mailbox; visitors can always keep exactly what they typed.
      const domains = {
        'gamil.com': 'gmail.com', 'gmial.com': 'gmail.com', 'gmai.com': 'gmail.com',
        'gmaill.com': 'gmail.com', 'gmail.con': 'gmail.com', 'gmail.co': 'gmail.com',
        'gnail.com': 'gmail.com', 'gmal.com': 'gmail.com',
        'yaho.com': 'yahoo.com', 'yahoo.con': 'yahoo.com', 'yhaoo.com': 'yahoo.com',
        'hotmial.com': 'hotmail.com', 'hotmai.com': 'hotmail.com', 'hotmail.con': 'hotmail.com',
        'outlok.com': 'outlook.com', 'outllook.com': 'outlook.com', 'outlook.con': 'outlook.com',
        'icloud.con': 'icloud.com', 'iclod.com': 'icloud.com'
      };
      const panel = document.createElement('div');
      panel.className = 'wl-email-check';
      panel.hidden = true;
      const message = document.createElement('p');
      message.setAttribute('role', 'status');
      const actions = document.createElement('div');
      actions.className = 'wl-email-check-actions';
      const use = document.createElement('button');
      const keep = document.createElement('button');
      use.type = keep.type = 'button';
      use.textContent = this.dataset.emailUse;
      keep.textContent = this.dataset.emailKeep;
      actions.append(use, keep);
      panel.append(message, actions);
      if (before) anchor.before(panel);
      else anchor.after(panel);
      const key = () => getEmail()?.value.trim().toLowerCase() || '';
      const suggestion = () => {
        const value = getEmail()?.value.trim() || '';
        const split = value.lastIndexOf('@');
        const domain = domains[value.slice(split + 1).toLowerCase()];
        return split > 0 && domain && !this.acceptedEmails.has(key()) ? value.slice(0, split + 1) + domain : '';
      };
      const show = () => {
        const next = suggestion();
        panel.hidden = !next;
        message.textContent = next ? `${this.dataset.emailQuestion} ${next}?` : '';
        return next;
      };
      const options = { signal: this.controller.signal };
      root.addEventListener('input', (event) => {
        if (event.target === getEmail()) show();
      }, options);
      root.addEventListener('submit', (event) => {
        if (!show()) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        use.focus();
      }, { ...options, capture: true });
      use.addEventListener('click', () => {
        const email = getEmail();
        const next = suggestion();
        if (!email || !next) return;
        const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set;
        setter.call(email, next);
        email.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
        email.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
        panel.hidden = true;
        email.focus();
      }, options);
      keep.addEventListener('click', () => {
        this.acceptedEmails.add(key());
        panel.hidden = true;
        getEmail()?.focus();
      }, options);
      this.controller.signal.addEventListener('abort', () => panel.remove(), { once: true });
    }
    disconnectedCallback() {
      clearInterval(this.readyTimer);
      this.controller?.abort();
      this.controller = null;
      document.documentElement.classList.remove('wl-profile-scroll-lock');
    }
  });
}
